import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ConciergeWidgetChat } from './ConciergeWidgetChat';
import { ConciergeWidgetState, PageContext } from './types/widget';
import { useConciergeSettings } from './settings';
import { useConciergeContext } from './ConciergeContextProvider';
import { useVisualViewportInsets } from './hooks/useVisualViewportInsets';
import { useScrollChainGuard } from './hooks/useScrollChainGuard';
import { DESKTOP_SHEET_HEIGHT_PX, SKIRT_DROP_PX, mobileSheetGeometry } from './mobileSheetGeometry';

export interface ConciergeWidgetProps {
  widgetState: ConciergeWidgetState;
  pageContext?: PageContext;
  showMaximize?: boolean;
  /** Whether the widget is currently in maximized (modal) mode */
  isMaximized?: boolean;
  onMaximize?: () => void;
  /** Called when user clicks minimize button in maximized mode */
  onMinimize?: () => void;
  title?: string;
  isConciergeEnabled?: boolean;
  /**
   * When true, renders directly without portal (for use inside ShadowDomWrapper)
   * When false (default), renders via portal to document.body
   */
  disablePortal?: boolean;
  /** Initial query to auto-send when widget opens (passed via props to avoid cross-bundle Zustand issues) */
  initialQuery?: string;
  /** Stackline SKU associated with the initial query (for product context) */
  initialQueryStacklineSku?: string;
  /** Called after the initial query has been processed/sent */
  onInitialQueryProcessed?: () => void;
  /** Brand ID for fetching suggested queries. If not provided, will try to get from brand config */
  brandId?: number;
  /** Explicit override for the dedicated concierge page URL (expand/history icons).
   *  If not provided, the chat widget resolves the server-computed `canonicalConciergeUrl`
   *  from the brand-app config (Plan 6), falling back to origin + /concierge. */
  conciergePageUrl?: string;
}

export const ConciergeWidget: React.FC<ConciergeWidgetProps> = ({
  widgetState,
  pageContext,
  showMaximize = true,
  isMaximized = false,
  onMaximize,
  onMinimize,
  title,
  isConciergeEnabled = true,
  disablePortal = false,
  initialQuery,
  initialQueryStacklineSku,
  onInitialQueryProcessed,
  brandId,
  conciergePageUrl,
}) => {
  const { isOpen, close } = widgetState;
  const widgetRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(isOpen);

  // Get positioning settings from window.brandclubSettings.concierge
  const settings = useConciergeSettings();

  // The mobile sheet is sized AND placed from the VISUAL viewport, not `100vh`.
  // The height comes from `vv.height`, which already accounts for the keyboard;
  // the inset places it, because iOS shrinks the LAYOUT viewport for the keyboard
  // only some of the time (first open yes, reopen no — see `mobileSheetGeometry`).
  const { height: visibleHeight, keyboardInset } = useVisualViewportInsets(isOpen && isMobile);

  // D-87 — bubble surface owns the voice overlay. The actual `<VoiceCallOverlay>`
  // mount lives inside `ConciergeWidgetChat`'s chat-body region (a sibling of the
  // chat messages, *below* `<ConciergeWidgetHeader>`) so the bubble header stays
  // visible during a call. ConciergeWidget only needs to coordinate the
  // close-bubble-mid-call discipline: if the user dismisses the bubble while a
  // call is live, fire `closeVoiceCall` *before* the close animation runs so the
  // overlay unmounts cleanly and `useVoiceSession` tears down WebRTC + mic +
  // posts `/voice/end` synchronously, leaving `voiceCallOpen` state consistent.
  const { voiceCallOpen, closeVoiceCall } = useConciergeContext();

  // Ref-backed so `handleAnimatedClose` (and the event listeners that
  // capture it) reads the latest value without forcing the listeners
  // to re-attach on every voice-state flip.
  const voiceCallOpenRef = useRef(voiceCallOpen);
  voiceCallOpenRef.current = voiceCallOpen;

  // Handle open/close transitions with animations
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
    }
  }, [isOpen]);

  // Animated close handler
  const handleAnimatedClose = () => {
    if (voiceCallOpenRef.current) {
      closeVoiceCall();
    }
    setIsClosing(true);
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      close();
      setShouldRender(false);
      setIsClosing(false);
    }, 250); // Match animation duration
  };

  // Track window width for responsive behavior
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Move focus into the panel when it opens and hand it back to the launcher
  // when it closes. Without this, opening the chat leaves focus behind on the
  // page: a keyboard or screen-reader user has to tab through the whole host
  // page to reach the conversation they just opened, and on close they land at
  // the top of the document instead of back on the launcher they came from.
  // Focus goes to the panel itself rather than the composer so opening the chat
  // does not throw up the on-screen keyboard on a phone.
  useEffect(() => {
    if (!shouldRender || isClosing) return;
    const panel = widgetRef.current;
    if (!panel) return;
    const raf = requestAnimationFrame(() => panel.focus({ preventScroll: true }));
    return () => {
      cancelAnimationFrame(raf);
      const launcher = (panel.getRootNode() as Document | ShadowRoot).querySelector(
        '[data-concierge-launcher]',
      ) as HTMLElement | null;
      launcher?.focus({ preventScroll: true });
    };
  }, [shouldRender, isClosing]);

  // Keep wheel scrolling inside the panel — the host page must not scroll
  // behind it once the panel's own content runs out. `overscroll-behavior`
  // alone cannot do this (see the hook).
  useScrollChainGuard(widgetRef, shouldRender && !isClosing);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isClosing) {
        handleAnimatedClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isClosing]);

  // Handle click outside to close (desktop widget mode only - mobile and maximized have backdrops)
  useEffect(() => {
    if (!isOpen || isMobile || isMaximized || isClosing) return;

    const handleClickOutside = (e: MouseEvent) => {
      // Use composedPath() to correctly detect clicks inside Shadow DOM
      const path = e.composedPath();

      // Check if click is inside the widget container (works with Shadow DOM)
      const isInsideWidget = path.some((element) => {
        if (element instanceof HTMLElement) {
          // Check for the widget container class or the shadow host
          return (
            element.classList?.contains('concierge-widget-container') ||
            element.id === 'concierge-shadow-host' ||
            element.id === 'concierge-mount'
          );
        }
        return false;
      });

      if (isInsideWidget) {
        return; // Click was inside the widget, don't close
      }

      // Don't close if clicking on the floating button (it has its own toggle logic).
      // Matched on `data-concierge-launcher`, not on the aria-label: the label is
      // brand-configurable copy and flips between open/close states, so keying
      // behaviour off it silently broke this guard.
      const isOnFloatingButton = path.some((element) => {
        if (element instanceof HTMLElement) {
          return element.hasAttribute('data-concierge-launcher');
        }
        return false;
      });

      if (isOnFloatingButton) {
        return;
      }

      handleAnimatedClose();
    };

    // Use mousedown for better UX (closes immediately on click, not on release)
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, isMobile, isMaximized, isClosing]);

  // Prevent body scroll when widget is open on mobile or maximized.
  //
  // On mobile this has to be the position-fixed lock, not `overflow: hidden`:
  // iOS Safari happily scrolls the document anyway when it decides a focused
  // field needs revealing, which is what yanked the page (and the sheet with
  // it) sideways as soon as the keyboard opened. Taking the document out of
  // flow leaves nothing for it to scroll, so the only thing that moves is the
  // sheet resizing with the visible viewport — deliberately, and under our control.
  // Desktop/maximized keeps the cheaper `overflow: hidden` it has always used.
  useEffect(() => {
    const { body } = document;
    if (!isOpen || !(isMobile || isMaximized)) {
      body.style.overflow = '';
      return;
    }

    if (!isMobile) {
      body.style.overflow = 'hidden';
      return () => {
        body.style.overflow = '';
      };
    }

    const scrollY = window.scrollY;
    const prev = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    };
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';

    return () => {
      body.style.overflow = prev.overflow;
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      // Restoring `position` snaps the document back to scroll 0 — put the
      // reader back where they were before they tapped the bubble.
      window.scrollTo(0, scrollY);
    };
  }, [isOpen, isMobile, isMaximized]);

  // Hide widget when not enabled or not rendering
  if (!isConciergeEnabled || !shouldRender) {
    return null;
  }

  // One geometry for the sheet AND the skirt behind it: the skirt has to ride the
  // sheet's anchor, or it stops covering the sheet's bottom edge the moment the
  // sheet is lifted.
  const sheet = mobileSheetGeometry({ visibleHeight, keyboardInset });

  // Compute styles based on mode (mobile, maximized, or default widget)
  const getContainerStyles = (): React.CSSProperties => {
    if (isMobile) {
      return {
        position: 'fixed',
        zIndex: settings.z_index,
        // Sit ON the keyboard instead of behind it.
        bottom: sheet.bottom,
        right: 0,
        left: 0,
        height: sheet.height,
        maxHeight: sheet.maxHeight,
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
        // No safe-area padding here: `ConciergeInputArea` already pads itself by
        // `max(1rem, env(safe-area-inset-bottom))`, so adding it again on the
        // frame would double-count the home indicator and float the composer.
        backgroundColor: 'white',
        borderRadius: '16px 16px 0 0',
        // Casts UPWARD, onto the page. The inherited `0 25px …` threw the shadow
        // DOWNWARD, past the sheet's bottom edge — invisible while the sheet
        // reached the bottom of the screen, but once the skirt was there to catch
        // it, it painted a grey gradient across it ("sometimes see some shadow at
        // the bottom"). A bottom sheet has nothing below it to shade.
        boxShadow: '0 -25px 50px -12px rgb(0 0 0 / 0.25)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        overscrollBehavior: 'contain',
        // Ride the keyboard's own ~250ms curve. The open/close animations drive
        // `transform`, so they don't collide with this.
        transition: 'bottom 0.25s ease-out, height 0.25s ease-out',
      };
    }

    if (isMaximized) {
      return {
        position: 'fixed',
        zIndex: settings.z_index,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 'min(768px, 90vw)',
        height: 'min(700px, 85vh)',
        maxWidth: '90vw',
        maxHeight: '85vh',
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        overscrollBehavior: 'contain',
      };
    }

    // Default widget mode - position based on alignment setting
    // Position widget above the floating button (60px button + 12px gap)
    const buttonHeight = 60;
    const gap = 12;
    const bottomOffset = settings.vertical_padding + buttonHeight + gap;

    const positionStyles =
      settings.alignment === 'left'
        ? { left: settings.horizontal_padding, right: 'auto' }
        : { right: settings.horizontal_padding, left: 'auto' };

    return {
      position: 'fixed',
      zIndex: settings.z_index,
      bottom: bottomOffset,
      ...positionStyles,
      height: `${DESKTOP_SHEET_HEIGHT_PX}px`,
      maxHeight: `calc(100vh - ${bottomOffset + settings.vertical_padding}px)`,
      width: '430px',
      minWidth: '430px',
      maxWidth: '430px',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
        overscrollBehavior: 'contain',
    };
  };

  // Determine animation class based on mode and closing state
  const getAnimationClass = (): string => {
    if (isClosing) {
      if (isMaximized) {
        return 'tw:animate-modal-collapse';
      }
      if (isMobile) {
        return 'tw:animate-slide-down-mobile';
      }
      return 'tw:animate-slide-down';
    }
    if (isMaximized) {
      return 'tw:animate-modal-expand';
    }
    return 'tw:animate-slide-up';
  };

  // Determine backdrop animation class
  const getBackdropAnimationClass = (): string => {
    if (isClosing) {
      return 'tw:animate-fade-out';
    }
    return 'tw:animate-fade-in';
  };

  const widget = (
    <>
      {/* Backdrop for mobile or maximized mode */}
      {(isMobile || isMaximized) && (
        <div
          className={`tw:fixed tw:inset-0 ${getBackdropAnimationClass()}`}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: isMaximized ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.5)',
            zIndex: settings.z_index - 1,
          }}
          onClick={isClosing ? undefined : isMaximized ? onMinimize : handleAnimatedClose}
        />
      )}
      {/*
        Skirt: a strip of the sheet's own colour pinned to the very bottom, BEHIND
        the sheet.

        On iOS the sheet's bottom edge does not always reach the bottom of the
        visible area, and the reported symptom is what shows in the gap — "under
        the input box, it is showing backdrop instead of white". Every attempt to
        compute the gap away failed on the device: `position: fixed` there is
        anchored to the visible area in some states and the layout viewport in
        others, `getBoundingClientRect()` reports LAYOUT coordinates while
        `visualViewport` reports the visible box, and iOS pans between the two
        while the keyboard is open. No ancestor creates a containing block
        (checked), so the cause is still unexplained.

        This does not need the cause. When the sheet reaches the bottom the strip
        is entirely behind it and invisible; when it falls short, the shortfall is
        sheet-white instead of dimmed page. Purely cosmetic, and it cannot be
        wrong in a way that moves anything.
      */}
      {isMobile && !isMaximized && (
        <div
          aria-hidden
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            // Hangs from the SHEET's bottom edge, not the layout viewport's.
            // The sheet is lifted by `keyboardInset` (347px on a reopen), and a
            // skirt pinned to `bottom: 0` is then nowhere near the edge it is
            // meant to cover — which is why the band came back once the lift was
            // restored. Anchored 240px below the sheet's own edge, it covers any
            // residual on that side in every state.
            bottom: sheet.bottom - SKIRT_DROP_PX,
            height: SKIRT_DROP_PX + 160,
            backgroundColor: 'white',
            // Same layer as the backdrop, but later in the DOM, so it paints over
            // the dimming and under the sheet.
            zIndex: settings.z_index - 1,
            pointerEvents: 'none',
          }}
        />
      )}
      {/* Widget container */}
      <div
        ref={widgetRef}
        role="dialog"
        // Modal only when it actually covers the page. The desktop bubble sits
        // alongside the page and the page stays usable behind it, so claiming
        // `aria-modal` there would wrongly hide the rest of the site from AT.
        aria-modal={isMobile || isMaximized ? true : undefined}
        aria-label={title || 'Concierge chat'}
        // Focus target when the panel opens (see the focus-management effect).
        tabIndex={-1}
        className={`concierge-widget-container tw:fixed tw:bg-white tw:shadow-2xl tw:flex tw:flex-col tw:overflow-hidden ${getAnimationClass()}`}
        // `outline: none` is correct on this one: the panel is a scripted focus
        // target (tabIndex -1), never a tab stop, so a ring drawn around the
        // whole sheet would be noise rather than a focus cue.
        style={{ ...getContainerStyles(), outline: 'none' }}
      >
        <ConciergeWidgetChat
          widgetState={{
            ...widgetState,
            close: handleAnimatedClose, // Use animated close
          }}
          pageContext={pageContext}
          showMaximize={showMaximize}
          isMaximized={isMaximized}
          onMaximize={onMaximize}
          onMinimize={onMinimize}
          title={title}
          initialQuery={initialQuery}
          initialQueryStacklineSku={initialQueryStacklineSku}
          onInitialQueryProcessed={onInitialQueryProcessed}
          brandId={brandId}
          conciergePageUrl={conciergePageUrl}
        />
      </div>
      {/* Animation keyframes - injected for both portal and direct render */}
      <style>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(1rem);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slide-down {
          from {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          to {
            opacity: 0;
            transform: translateY(1rem) scale(0.95);
          }
        }
        @keyframes slide-down-mobile {
          from {
            opacity: 1;
            transform: translateY(0);
          }
          to {
            opacity: 0;
            transform: translateY(100%);
          }
        }
        @keyframes modal-expand {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
        @keyframes modal-collapse {
          from {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
          to {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.9);
          }
        }
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes fade-out {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }
        .tw\\:animate-slide-up {
          animation: slide-up 0.3s ease-out forwards;
        }
        .tw\\:animate-slide-down {
          animation: slide-down 0.25s ease-in forwards;
        }
        .tw\\:animate-slide-down-mobile {
          animation: slide-down-mobile 0.25s ease-in forwards;
        }
        .tw\\:animate-modal-expand {
          animation: modal-expand 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .tw\\:animate-modal-collapse {
          animation: modal-collapse 0.25s ease-in forwards;
        }
        .tw\\:animate-fade-in {
          animation: fade-in 0.2s ease-out forwards;
        }
        .tw\\:animate-fade-out {
          animation: fade-out 0.2s ease-in forwards;
        }
      `}</style>
    </>
  );

  // Either render directly or via portal
  if (disablePortal) {
    return widget;
  }

  return createPortal(widget, document.body);
};
